import { CMSModal } from "@/context";
import Button from "@/designs/Button";
import Input from "@/designs/Input";
import Loader from "@/designs/Loader";
import Modal from "@/designs/Modal";
import {
  AddBookingSpaceDocument,
  GetBookingSpacesDocument,
  useAddBookingSpaceMutation,
  useGetSpaceCategoriesQuery,
  useGetSpaceResourceQuery,
  useGetSpacesQuery,
  useGetUsersQuery,
} from "@/graphql/generated/schema";
import { Divider, Form, Select } from "antd";
import React, { useContext, useMemo, useState } from "react";
import toast from "react-hot-toast";

export default function NewBooking({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { selectedLocation } = useContext(CMSModal);
  const [form] = Form.useForm();
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [spaceId, setSpaceId] = useState<string | undefined>(undefined);
  const [categoryId, setCategoryId] = useState<string | undefined>(undefined);

  const { data: usersData, loading: usersLoading } = useGetUsersQuery({
    variables: {
      search: employeeSearch || undefined,
      location: selectedLocation,
      limit: 20,
      offset: 0,
    },
    skip: !selectedLocation,
  });

  const { data: spacesData } = useGetSpacesQuery({
    variables: { location: selectedLocation },
    skip: !selectedLocation,
  });

  const { data: catData } = useGetSpaceCategoriesQuery({
    variables: { location: selectedLocation },
    skip: !selectedLocation,
  });

  const { data: resData, loading: resourcesLoading } = useGetSpaceResourceQuery(
    {
      variables: {
        location: selectedLocation,
        space: spaceId,
        resourceCategory: categoryId,
      },
      skip: !selectedLocation || !categoryId,
    },
  );

  const [addBooking, { loading }] = useAddBookingSpaceMutation({
    refetchQueries: [
      {
        query: GetBookingSpacesDocument,
        variables: { location: selectedLocation },
      },
    ],
  });

  const handleSubmit = async (values: any) => {
    try {
      if (!selectedLocation) return;
      const startDate = values?.startDate;
      const startTime = values?.startTime;
      const endDate = values?.endDate;
      const endTime = values?.endTime;

      const toIso = (d?: string, t?: string) => {
        if (!d || !t) return undefined;
        // Interpret as local time, then send ISO string
        const dt = new Date(`${d}T${t}`);
        if (Number.isNaN(dt.getTime())) return undefined;
        return dt.toISOString();
      };

      const input: any = {
        start: toIso(startDate, startTime) ?? null,
        space: values?.space ?? null,
        resource: values?.resource ?? null,
        location: selectedLocation ?? null,
        end: toIso(endDate, endTime) ?? null,
        employee: values?.employee ?? null,
        category: values?.category ?? null,
      };

      const res = await addBooking({ variables: { input } });
      const output = res.data?.addBookingSpace;
      if (output?.booking?._id) {
        toast.success("Booking created");
        form.resetFields();
        setSpaceId(undefined);
        onClose();
      } else if (output?.error) {
        toast.error(output.error.message);
      } else {
        toast.error("Something went wrong");
      }
    } catch (e: any) {
      toast.error(e?.message || "Something went wrong");
    }
  };

  const spaceOptions = useMemo(
    () =>
      (spacesData?.getSpaces ?? [])
        .filter(Boolean)
        .map((s: any) => ({ value: s?._id as string, label: s?.name as string })),
    [spacesData],
  );

  const categoryOptions = useMemo(
    () =>
      (catData?.getSpaceCategories ?? [])
        .filter(Boolean)
        .map((c: any) => ({ value: c?._id as string, label: c?.name as string })),
    [catData],
  );

  const resourceOptions = useMemo(
    () =>
      (resData?.getSpaceResource ?? [])
        .filter(Boolean)
        .map((r: any) => ({ value: r?._id as string, label: r?.name as string })),
    [resData],
  );

  const userOptions = useMemo(
    () =>
      (usersData?.getUsers?.user ?? []).filter(Boolean).map((u: any) => ({
        value: u?.id as string,
        label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "-",
      })),
    [usersData],
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New Booking">
      {usersLoading && <Loader />}
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          label="Who is this booking for?"
          name="employee"
          rules={[{ required: true, message: "Please select an employee" }]}
        >
          <Select
            showSearch
            placeholder="Search for an employee"
            onSearch={(v) => setEmployeeSearch(v)}
            options={userOptions}
            loading={usersLoading}
          />
        </Form.Item>

        <Form.Item
          label="What are you booking?"
          name="category"
          rules={[{ required: true, message: "Please select a category" }]}
        >
          <Select
            placeholder="Select category"
            options={categoryOptions}
            onChange={(v) => {
              setCategoryId(v || undefined);
              form.setFieldValue("resource", undefined);
            }}
          />
        </Form.Item>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Form.Item label="Space" name="space">
            <Select
              placeholder="Any"
              allowClear
              options={spaceOptions}
              onChange={(v) => setSpaceId(v || undefined)}
            />
          </Form.Item>

          <Form.Item label="Resource" name="resource">
            <Select placeholder="Any" allowClear options={resourceOptions} loading={resourcesLoading} />
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Form.Item
            label="Start date"
            name="startDate"
            rules={[{ required: true, message: "Please select start date" }]}
          >
            <Input type="date" />
          </Form.Item>

          <Form.Item
            label="Start time"
            name="startTime"
            rules={[{ required: true, message: "Please select start time" }]}
          >
            <Input type="time" />
          </Form.Item>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Form.Item
            label="End date"
            name="endDate"
            rules={[{ required: true, message: "Please select end date" }]}
          >
            <Input type="date" />
          </Form.Item>

          <Form.Item
            label="End time"
            name="endTime"
            rules={[{ required: true, message: "Please select end time" }]}
          >
            <Input type="time" />
          </Form.Item>
        </div>

        <Divider />

        <Button type="submit" className="lg:w-44">
          Create Booking
        </Button>
      </Form>
    </Modal>
  );
}
